'use client'

/**
 * BudgetHistoryPanel — SlideOutPanel-based transaction history.
 *
 * Replaces the old hand-rolled TransactionsDialog popover that lived inside
 * BudgetHero. Now rendered via the /budget page sticky header's "History"
 * button and uses the shared SlideOutPanel (proper desktop/mobile split).
 *
 * Tabs: Recent · Top-ups · Smart Spend (conditional)
 */

import { useEffect, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowUp01Icon,
  ArrowDown01Icon,
  Clock01Icon,
  CheckmarkCircle01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { cn } from '@/lib/utils'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'

// ── Types ──────────────────────────────────────────────────────────────────

interface Transaction {
  id: string
  type: string
  amount_cents: number
  description: string | null
  created_at: string
}

interface SmartSpendDecision {
  id: string
  decision_date: string
  diff_preview_status: 'pending' | 'approved' | 'rejected' | null
  allocations: Record<string, number>
}

interface BudgetHistoryPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  smartSpendEnabled: boolean
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// ── Component ──────────────────────────────────────────────────────────────

export function BudgetHistoryPanel({
  open,
  onOpenChange,
  smartSpendEnabled,
}: BudgetHistoryPanelProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [decisions, setDecisions] = useState<SmartSpendDecision[]>([])
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState<'recent' | 'topups' | 'smartspend'>('recent')

  useEffect(() => {
    if (!open) return
    setLoading(true)

    const fetches: Promise<void>[] = [
      fetch('/api/wallet')
        .then((r) => r.json())
        .then((d: { transactions?: Transaction[] }) => {
          setTransactions(d.transactions ?? [])
        })
        .catch(() => {}),
    ]

    if (smartSpendEnabled) {
      fetches.push(
        fetch('/api/smart-spend/decisions?limit=20')
          .then((r) => (r.ok ? r.json() : { decisions: [] }))
          .then((d: { decisions?: SmartSpendDecision[] }) => {
            setDecisions(d.decisions ?? [])
          })
          .catch(() => {}),
      )
    }

    void Promise.all(fetches).finally(() => setLoading(false))
  }, [open, smartSpendEnabled])

  const topups = transactions.filter((t) => t.type === 'deposit')
  const recent = transactions.slice(0, 20)
  const pendingDecisions = decisions.filter((d) => d.diff_preview_status === 'pending')
  const resolvedDecisions = decisions.filter((d) => d.diff_preview_status !== 'pending')

  const handleDecisionAction = async (decisionId: string, action: 'approve' | 'reject') => {
    await fetch('/api/smart-spend/diff-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, decision_id: decisionId }),
    })
    setDecisions((prev) =>
      prev.map((d) =>
        d.id === decisionId
          ? { ...d, diff_preview_status: action === 'approve' ? 'approved' : 'rejected' }
          : d,
      ),
    )
  }

  const tabs = [
    { key: 'recent' as const, label: 'Recent' },
    { key: 'topups' as const, label: 'Top-ups' },
    ...(smartSpendEnabled ? [{ key: 'smartspend' as const, label: 'Smart Spend' }] : []),
  ]

  return (
    <SlideOutPanel
      open={open}
      onOpenChange={onOpenChange}
      title="History"
      direction="right"
    >
      {/* Tab bar */}
      <div className="flex border-b border-atrium-border px-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'py-2.5 px-1 mr-5 text-sm border-b-2 transition-colors',
              tab === t.key
                ? 'border-atrium-black text-atrium-black font-medium'
                : 'border-transparent text-atrium-muted hover:text-atrium-black',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div>
        {loading && (
          <div className="flex items-center justify-center h-32 text-sm text-atrium-muted">
            Loading…
          </div>
        )}

        {!loading && tab === 'recent' && (
          <div className="divide-y divide-atrium-border">
            {recent.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-atrium-muted">
                No transactions yet.
              </p>
            )}
            {recent.map((tx) => (
              <TxRow key={tx.id} tx={tx} />
            ))}
          </div>
        )}

        {!loading && tab === 'topups' && (
          <div className="divide-y divide-atrium-border">
            {topups.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-atrium-muted">
                No top-ups yet.
              </p>
            )}
            {topups.map((tx) => (
              <TxRow key={tx.id} tx={tx} />
            ))}
          </div>
        )}

        {!loading && tab === 'smartspend' && (
          <div className="divide-y divide-atrium-border">
            {pendingDecisions.length > 0 && (
              <>
                <div className="px-5 py-2 bg-amber-50 text-xs font-medium text-amber-700">
                  Pending review
                </div>
                {pendingDecisions.map((d) => (
                  <DecisionRow key={d.id} decision={d} onAction={handleDecisionAction} />
                ))}
              </>
            )}
            {resolvedDecisions.length > 0 && (
              <>
                <div className="px-5 py-2 bg-atrium-content text-xs font-medium text-atrium-muted">
                  History
                </div>
                {resolvedDecisions.map((d) => (
                  <DecisionRow key={d.id} decision={d} onAction={handleDecisionAction} />
                ))}
              </>
            )}
            {decisions.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-atrium-muted">
                No Smart Spend decisions yet.
              </p>
            )}
          </div>
        )}
      </div>
    </SlideOutPanel>
  )
}

// ── TxRow ──────────────────────────────────────────────────────────────────

function TxRow({ tx }: { tx: Transaction }) {
  const positive = tx.amount_cents > 0
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <div
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          positive ? 'bg-emerald-50 text-emerald-700' : 'bg-atrium-content text-atrium-muted',
        )}
      >
        <HugeiconsIcon
          icon={positive ? ArrowUp01Icon : ArrowDown01Icon}
          className="size-3.5"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-atrium-black">
          {tx.description ?? tx.type.replace(/_/g, ' ')}
        </div>
        <div className="text-[11px] text-atrium-muted">{fmtDate(tx.created_at)}</div>
      </div>
      <div
        className={cn(
          'font-mono text-sm tabular-nums shrink-0',
          positive ? 'text-emerald-700' : 'text-atrium-black',
        )}
      >
        {positive ? '+' : ''}
        {fmtCents(Math.abs(tx.amount_cents))}
      </div>
    </div>
  )
}

// ── DecisionRow ────────────────────────────────────────────────────────────

function DecisionRow({
  decision,
  onAction,
}: {
  decision: SmartSpendDecision
  onAction: (id: string, action: 'approve' | 'reject') => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const isPending = decision.diff_preview_status === 'pending'
  const isApproved = decision.diff_preview_status === 'approved'

  const act = async (action: 'approve' | 'reject') => {
    setBusy(true)
    await onAction(decision.id, action)
    setBusy(false)
  }

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <HugeiconsIcon
            icon={isPending ? Clock01Icon : CheckmarkCircle01Icon}
            className={cn(
              'size-4',
              isPending ? 'text-amber-500' : isApproved ? 'text-emerald-600' : 'text-atrium-muted',
            )}
          />
          <span className="text-sm text-atrium-black font-medium">
            {new Date(decision.decision_date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-xs text-atrium-muted capitalize">
            {decision.diff_preview_status ?? 'pending'}
          </span>
        </div>
        {isPending && (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void act('approve')}
              className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void act('reject')}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              Reject
            </button>
          </div>
        )}
      </div>
      <div className="text-xs text-atrium-muted">
        {Object.entries(decision.allocations)
          .slice(0, 3)
          .map(([key, val]) => (
            <span key={key} className="mr-2">
              {key}: {fmtCents(Number(val))}
            </span>
          ))}
        {Object.keys(decision.allocations).length > 3 && (
          <span className="text-atrium-muted/60">
            +{Object.keys(decision.allocations).length - 3} more
          </span>
        )}
      </div>
    </div>
  )
}
